export type LabelRect={x:number;y:number;width:number;height:number};
export type ActorLabel=LabelRect&{id:string;priority:number;required?:boolean;previous?:{dx:number;dy:number}};
export type LabelPlacement={id:string;x:number;y:number;visible:boolean;dx:number;dy:number};
function intersects(a:LabelRect,b:LabelRect):boolean{
 return a.x<b.x+b.width+4&&a.x+a.width+4>b.x&&a.y<b.y+b.height+4&&a.y+a.height+4>b.y;
}
/** Labels use projected world coordinates. Never modify authoritative actor positions. */
export function layoutActorLabels(labels:ActorLabel[],reserved:LabelRect[]=[],viewport?:LabelRect):LabelPlacement[]{
 const occupied=[...reserved],result:LabelPlacement[]=[];
 for(const label of [...labels].sort((a,b)=>b.priority-a.priority||a.id.localeCompare(b.id))){
  // A camera-edge fallback may be far from the actor. Reclaim a nearby anchor once the camera settles.
  const nearbyPrevious=label.previous&&Math.abs(label.previous.dx)<=320&&Math.abs(label.previous.dy)<=120;
  const options=[...(nearbyPrevious?[label.previous!]:[]),...[0,-24,-48,-72,-96,-120].map(dy=>({dx:0,dy})),...[-1,1].flatMap(side=>[80,160,...(label.required?[240,320]:[])].flatMap(dx=>[0,-24].map(dy=>({dx:side*dx,dy}))))];
  let place=options.find(offset=>{
   const rect={...label,x:label.x+offset.dx,y:label.y+offset.dy};
   const inside=!viewport||(rect.x>=viewport.x&&rect.y>=viewport.y&&rect.x+rect.width<=viewport.x+viewport.width&&rect.y+rect.height<=viewport.y+viewport.height);
   return inside&&!occupied.some(other=>intersects(rect,other));
  });
  // Required identities remain visible even at pathological densities. Other names can be selected to gain priority.
  if(!place&&label.required){
   if(viewport&&label.width<=viewport.width&&label.height<=viewport.height){
    const clampX=Math.max(viewport.x,Math.min(label.x,viewport.x+viewport.width-label.width));
    const clampY=Math.max(viewport.y,Math.min(label.y-120,viewport.y+viewport.height-label.height));
    const fallback:typeof options=[];
    for(let y=viewport.y;y+label.height<=viewport.y+viewport.height;y+=label.height+4){
     for(const x of [clampX,viewport.x,viewport.x+viewport.width-label.width])fallback.push({dx:x-label.x,dy:y-label.y});
    }
    fallback.sort((a,b)=>Math.abs(a.dy)-Math.abs(b.dy)||Math.abs(a.dx)-Math.abs(b.dx));
    place=fallback.find(offset=>!occupied.some(other=>intersects({...label,x:label.x+offset.dx,y:label.y+offset.dy},other)))??{dx:clampX-label.x,dy:clampY-label.y};
   }else place={dx:0,dy:-120};
  }
  if(!place){result.push({id:label.id,x:label.x,y:label.y,visible:false,dx:0,dy:0});continue;}
  const rect={...label,x:label.x+place.dx,y:label.y+place.dy};occupied.push(rect);
  result.push({id:label.id,x:rect.x,y:rect.y,visible:true,...place});
 }
 return result;
}
